// src/pages/Login.tsx
// Updated: 2026-08-04 20:50:00 CDT
// Purpose: Secure Supabase email login with status-only diagnostics and accessible styling.

import React, { useEffect, useState } from "react"; 
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/auth/useAuth";

const ADMIN_EMAIL = "admin@tableread.tv";

const Login: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || "/admin";

  const [email, setEmail] = useState<string>(ADMIN_EMAIL);
  const [password, setPassword] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [message, setMessage] = useState<string>(
    typeof (location.state as { message?: unknown } | null)?.message === "string"
      ? (location.state as { message: string }).message
      : "",
  );

  useEffect(() => {
    if (!isLoading && user) {
      // eslint-disable-next-line no-console
      console.debug("[Login] user already logged in, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [isLoading, user, from, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setSubmitting(true);

    try {
      // eslint-disable-next-line no-console
      console.debug("[Login] Attempting signInWithPassword", { email: "***redacted***" });

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // eslint-disable-next-line no-console
        console.error("[Login] signInWithPassword error:", error);
        setMessage(error.message || "Login failed. Check your credentials.");
        return;
      }

      // eslint-disable-next-line no-console
      console.debug("[Login] signInWithPassword success.");
      setMessage("Login successful. Redirecting…");
      navigate(from, { replace: true });
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("[Login] Unexpected error:", err);
      setMessage(String(err?.message ?? "Unexpected error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6 bg-neutral-900">
      <div className="w-full max-w-md rounded-2xl shadow-xl bg-white p-8">
        <h1 className="text-2xl font-semibold mb-6 text-gray-900">
          TableRead.tv — Admin Login
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-800">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500
                         focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="admin@tableread.tv"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-800">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500
                         focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-2xl border border-gray-300 bg-gray-100 text-gray-900 px-4 py-2 font-medium
                       shadow-xs hover:bg-gray-200 hover:shadow-md disabled:opacity-60 focus:outline-hidden
                       focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        {!!message && (
          <p className="mt-4 text-sm text-gray-800" role="alert">
            {message}
          </p>
        )}

        <p className="mt-6 text-xs text-gray-600">
          Having trouble? Open DevTools → Console for status and sanitized error details.
        </p>
      </div>
    </div>
  );
};

export default Login;
